// Phase 2 — fetches the asset type ids that have been "Don't
// track"'d for the given room type, so the field-side AddAssetDialog
// can hide them from its picker.
//
// Wraps `GET /capture/room-types/:roomTypeId/excluded-asset-types`.
// Types intentionally redeclared (not imported from backend) per the
// monorepo dependency rules.

import { useState } from 'react'

import { ApiError, apiFetch, isAbortError } from './api'
import { useAbortableEffect } from './use-abortable-effect'

export interface RoomExclusionsResponse {
  assetTypeIds: string[]
}

export function fetchRoomExclusions(
  roomTypeId: string,
  init?: { signal?: AbortSignal },
): Promise<RoomExclusionsResponse> {
  return apiFetch<RoomExclusionsResponse>(
    `/capture/room-types/${encodeURIComponent(roomTypeId)}/excluded-asset-types`,
    init,
  )
}

export interface UseRoomExclusionsState {
  ids: ReadonlySet<string>
  loading: boolean
  error: string | null
}

const EMPTY_SET: ReadonlySet<string> = new Set()

// Hook for the capture flow. Returns a Set of excluded asset type
// ids for the given roomTypeId. Refresh-tick lets callers force a
// refetch (e.g. when the user reopens the dialog after dismissing
// elsewhere). When `roomTypeId` is null we never fetch — the picker
// gracefully falls back to no exclusions.
const EMPTY_STATE: UseRoomExclusionsState = {
  ids: EMPTY_SET,
  loading: false,
  error: null,
}

export function useRoomExclusions(
  roomTypeId: string | null,
  refreshTick = 0,
): UseRoomExclusionsState {
  const [fetched, setFetched] = useState<UseRoomExclusionsState>({
    ids: EMPTY_SET,
    loading: roomTypeId !== null,
    error: null,
  })

  useAbortableEffect(
    (signal) => {
      if (roomTypeId === null) return undefined
      // Reset `ids` on every room-type transition (Codex P1 on PR
      // #365). Keeping the previous room's ids while the new fetch
      // is in flight would briefly hide valid assets in the next
      // room — the AddAssetDialog filters synchronously off this
      // Set, so a stale entry blocks legitimate captures.
      setFetched({ ids: EMPTY_SET, loading: true, error: null })
      fetchRoomExclusions(roomTypeId, { signal })
        .then((response) => {
          setFetched({
            ids: new Set(response.assetTypeIds),
            loading: false,
            error: null,
          })
        })
        .catch((err: unknown) => {
          if (isAbortError(err)) return
          const message =
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : 'Unexpected error'
          setFetched({ ids: EMPTY_SET, loading: false, error: message })
        })
      return undefined
    },
    [roomTypeId, refreshTick],
  )

  // Derived: when no roomTypeId is selected, ignore any stale `fetched`
  // state from a prior selection and return the empty steady state.
  // Avoids `setState` inside an effect just to clear state.
  return roomTypeId === null ? EMPTY_STATE : fetched
}
